/**
 * Versão nova no servidor: avisar, e só atualizar quando o operador mandar.
 *
 * O service worker guarda o app no aparelho, então uma imagem nova no
 * servidor não chega sozinha: sem isto, ela só entraria quando alguém
 * fechasse o app inteiro — o que ninguém faz no meio de um evento.
 *
 * O que a atualização troca são os arquivos do app (código, WebAssembly,
 * fontes). **O banco não é tocado**: as validações ficam no OPFS e a sessão e
 * o caixa no armazenamento do navegador, nada disso passa pelo service
 * worker. Recarregar não perde validação pendente nem desloga.
 *
 * Por isso quem decide é o operador: a tela avisa, e a troca acontece no
 * toque dele — nunca no meio de uma leitura.
 */

import {useEffect, useState} from 'react';
import {registerSW} from 'virtual:pwa-register';

/**
 * De quanto em quanto tempo perguntar ao servidor se há versão nova. O
 * navegador já pergunta ao abrir o app; isto cobre a portaria que fica horas
 * com a mesma tela aberta.
 */
const INTERVALO_DE_CONSULTA_MS = 15 * 60 * 1000;

/** Quanto o "Depois" segura o aviso antes de ele voltar. */
const ESPERA_DO_DEPOIS_MS = 10 * 60 * 1000;

let temVersaoNova = false;
let aplicar: (() => Promise<void>) | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach(ouvinte => ouvinte());

/** Registra o service worker. Chamado uma vez, na entrada. */
export function vigiarAtualizacoes(): void {
  const atualizar = registerSW({
    immediate: true,
    onNeedRefresh() {
      console.log('[pwa] versão nova disponível');
      temVersaoNova = true;
      avisar();
    },
    onRegisteredSW(_url, registro) {
      console.log('[pwa] service worker registrado');
      if (registro) {
        setInterval(() => registro.update().catch(() => {}), INTERVALO_DE_CONSULTA_MS);
      }
    },
    onRegisterError: erro => console.warn(`[pwa] service worker falhou: ${erro}`),
  });

  /**
   * `registerSW` manda o worker novo assumir e recarrega a página quando ele
   * toma o controle — só que o worker novo ativa sem reivindicar os clientes,
   * e esse controle pode nunca trocar (foi o que aconteceu no teste: o aviso
   * sumia e a versão continuava a antiga). Recarregar aqui é o que garante a
   * troca, e é seguro: o banco das validações não passa pelo service worker.
   */
  aplicar = async () => {
    try {
      await atualizar(true);
    } catch (e) {
      console.warn(`[pwa] troca de versão falhou: ${e}`);
    }
    setTimeout(() => window.location.reload(), 600);
  };
}

export function useAtualizacao() {
  const [, setVersao] = useState(0);

  useEffect(() => {
    const ouvinte = () => setVersao(v => v + 1);
    ouvintes.add(ouvinte);
    return () => {
      ouvintes.delete(ouvinte);
    };
  }, []);

  return {
    temVersaoNova,
    atualizar: () => aplicar?.(),
    /**
     * "Depois": o aviso some e volta daqui a pouco. Não some de vez porque a
     * versão nova continua esperando, e um aparelho preso numa versão velha a
     * portaria inteira é pior que um aviso repetido.
     */
    adiar: () => {
      temVersaoNova = false;
      avisar();
      setTimeout(() => {
        temVersaoNova = true;
        avisar();
      }, ESPERA_DO_DEPOIS_MS);
    },
  };
}
